import React, { ReactElement, useState } from 'react';
import camelcase from 'camelcase-es5';

import InputLabel from '../InputLabel';
import InputHelper from '../InputHelper';

import useFormControlReducer from '../../hooks/useFormControlReducer';
import Box from '../../components/Box/Box';
import DropdownFormControl from './DropdownFormControl';
import DropdownFooter from './DropdownFooter';
import componentName from './name';

import TDropdownProps from './types/TDropdownProps';
import Icon from '../Icon/Icon';

const getValueProps = (isControlled: boolean, value: string | undefined, defaultValue: string) => ({
  [isControlled ? 'value' : 'defaultValue']: value ?? defaultValue,
});

const renderHelperOrErrorText = (errorText?: string, helperText?: string) => {
  if (errorText) {
    return <DropdownFooter errorText={errorText} />;
  }
  if (helperText) {
    return <InputHelper>{helperText}</InputHelper>;
  }
  return null;
};

const Dropdown = ({
  defaultValue = '',
  errorText,
  helperText,
  labelText = '',
  isRequired,
  id = `${componentName}-${camelcase(labelText)}`,
  inputRef,
  onChange,
  onFocus,
  onBlur,
  onMouseOut,
  onMouseOver,
  optionalText,
  options,
  optGroups,
  placeHolderText,
  value,
  disabled,
  ariaLabel,
  ...rest
}: TDropdownProps): ReactElement => {
  const { state, handlers } = useFormControlReducer({
    onChange,
    onFocus,
    onBlur,
    onMouseOut,
    onMouseOver,
    errorText,
    value,
  });

  const { isDirty, isEmpty, isErrored, isFocused, isHovered, isTouched } = state;
  const { handleChange, handleFocus, handleBlur, handleMouseOver, handleMouseOut } = handlers;

  const isControlled = typeof onChange === 'function' && typeof value !== 'undefined';
  const valueProps = getValueProps(isControlled, value, defaultValue);

  const [isExpanded, setIsExpanded] = useState(false);

  const hanldeOnFocus = (e: React.FocusEvent<HTMLSelectElement>) => {
    handleFocus(e);
    setIsExpanded(true);
  };

  const handleOnBlur = (e: React.FocusEvent<HTMLSelectElement>) => {
    handleBlur(e);
    setIsExpanded(false);
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    handleChange(e);
    setIsExpanded(false);
  };

  return (
    <Box as="div" name={componentName} {...rest}>
      {labelText && (
        <InputLabel htmlFor={id} isInputRequired={isRequired} labelText={labelText} optionalText={optionalText} />
      )}

      <DropdownFormControl
        isDirty={isDirty}
        isEmpty={isEmpty}
        isErrored={isErrored}
        isFocused={isFocused}
        isHovered={isHovered}
        isTouched={isTouched}
      >
        <Box
          as="select"
          {...valueProps}
          htmlNameAttr={id}
          id={id}
          name="input"
          parent={componentName}
          ref={inputRef}
          onChange={handleOnChange}
          onFocus={hanldeOnFocus}
          onBlur={handleOnBlur}
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          disabled={disabled}
          aria-label={ariaLabel}
        >
          {placeHolderText && (
            <option key="placeholder" value="" disabled>
              {placeHolderText}
            </option>
          )}
          {options?.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
          {optGroups?.map(({ label, options: optGroupOptions }) => (
            <optgroup key={label} label={label}>
              {optGroupOptions.map((option) => (
                <option key={option.value} value={option.value} disabled={option.disabled}>
                  {option.label}
                </option>
              ))}
            </optgroup>
          ))}
        </Box>
        <Icon name={isExpanded ? 'arrow-up-regular' : 'arrow-down-regular'} />
      </DropdownFormControl>

      {renderHelperOrErrorText(errorText, helperText)}
    </Box>
  );
};

export default Dropdown;
